import React, { useState, useEffect } from 'react';
import { devError } from '../lib/devLog';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistsPage = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const response = await fetch(`${API_URL}/api/artists?limit=100`);
      const data = await response.json();
      setArtists(data.sort((a, b) => a.name.localeCompare(b.name)));
      setLoading(false);
    } catch (error) {
      devError('Error fetching artists:', error);
      setLoading(false);
    }
  };

  const filteredArtists = selectedLetter === 'all' 
    ? artists 
    : artists.filter(artist => artist.name.charAt(0).toUpperCase() === selectedLetter);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground font-sans text-sm">Loading artists...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <Header />
      
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        {/* Page Title with Animation */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6 font-sans">
            Our Artists
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl text-foreground mb-4 tracking-tight">
            Artists
          </h1>
          <p className="font-sans text-base text-muted-foreground leading-relaxed max-w-2xl">
            Browse {artists.length} artists from emerging to iconic
          </p>
        </motion.div>

        {/* Alphabet Navigation with Morph Effect */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="border-t border-b border-border py-6 mb-12 sticky top-[80px] bg-background z-40 backdrop-blur-xl bg-background/80"
        >
          <div className="flex items-center gap-3 overflow-x-auto">
            <motion.button
              onClick={() => setSelectedLetter('all')}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`px-4 py-2 text-sm font-sans font-medium transition-all relative ${
                selectedLetter === 'all'
                  ? 'text-background'
                  : 'hover:text-muted-foreground'
              }`}
            >
              {selectedLetter === 'all' && (
                <motion.div
                  layoutId="selectedLetter"
                  className="absolute inset-0 bg-foreground rounded-sm"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              <span className="relative z-10">All</span>
            </motion.button>
            {alphabet.map((letter) => (
              <motion.button
                key={letter}
                onClick={() => setSelectedLetter(letter)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className={`px-4 py-2 text-sm font-sans font-medium transition-all relative ${
                  selectedLetter === letter
                    ? 'text-background'
                    : 'hover:text-muted-foreground'
                }`}
                data-testid={`filter-${letter}`}
              >
                {selectedLetter === letter && (
                  <motion.div
                    layoutId="selectedLetter"
                    className="absolute inset-0 bg-foreground rounded-sm"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <span className="relative z-10">{letter}</span>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Artists Grid with Stagger */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
          {filteredArtists.map((artist, index) => (
            <motion.div
              key={artist.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.4, 
                delay: index * 0.03,
                type: "spring"
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              data-testid={`artist-${artist.id}`}
            >
              <Link to={`/artists/${artist.slug || artist.id}`} className="group cursor-pointer block">
                <motion.div 
                  className="mb-4 bg-muted aspect-square rounded-full overflow-hidden relative"
                  whileHover={{ scale: 1.05 }}
                >
                  {artist.imageUrl ? (
                    <img
                      src={artist.imageUrl}
                      alt={artist.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-4xl font-serif text-muted-foreground">
                        {artist.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  {hoveredIndex === index && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute inset-0 bg-foreground/10"
                    />
                  )}
                </motion.div>
                <motion.p 
                  className="font-serif text-base text-center group-hover:text-muted-foreground transition-colors"
                  animate={{ y: hoveredIndex === index ? -2 : 0 }}
                >
                  {artist.name}
                </motion.p>
                <p className="text-xs text-muted-foreground text-center font-sans mt-1">
                  {artist.nationality && artist.birthYear 
                    ? `${artist.nationality}, ${artist.birthYear}`
                    : artist.nationality || artist.birthYear || ''}
                </p>
              </Link>
            </motion.div>
          ))}
        </div>

        {filteredArtists.length === 0 && (
          <div className="text-center py-20">
            <p className="font-sans text-muted-foreground">
              No artists found starting with "{selectedLetter}"
            </p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ArtistsPage;